import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  Button,
  Chip,
  Typography,
  Tabs,
  Tab,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Autocomplete,
  Snackbar,
  Alert,
  Skeleton,
  CircularProgress,
} from '@mui/material';
import {
  Add,
  PersonRemove,
  AccountBalanceWallet,
  HowToReg,
  CheckCircle,
  Cancel as CancelIcon,
  ArrowForward,
  ReceiptLong,
  WarningAmber,
} from '@mui/icons-material';

import hrPlusService from '../../services/hrPlusService';
import hrService, { EmployeeListItem } from '../../services/hrService';
import {
  PageHeader,
  StatCard,
  FilterBar,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  StatusTone,
  TypePill,
  ErrorState,
  TableEmptyState,
} from '../../components/common/pageKit';
import { formatIndianNumber, formatCurrency } from '../../utils/currency';

// ---------------------------------------------------------------------------
// Lifecycle hub — Separations/F&F, Loans & Advances, Onboarding. All three
// sit on migration 063 (hrLifecycle.js model/controller). Every list here is
// tenant-scoped server-side; this page only filters/paginates client-side on
// top of what the API already returns.
// ---------------------------------------------------------------------------

type SeparationStatus = 'INITIATED' | 'IN_CLEARANCE' | 'PENDING_FNF' | 'SETTLED' | 'CANCELLED';
type FnfStatus = 'DRAFT' | 'APPROVED' | 'PAID';
type LoanStatus = 'REQUESTED' | 'APPROVED' | 'ACTIVE' | 'CLOSED' | 'REJECTED';
type OnboardingStatus = 'PENDING' | 'IN_PROGRESS' | 'DONE' | 'SKIPPED';

interface Clearance {
  asset: boolean;
  finance: boolean;
  it: boolean;
  hr: boolean;
  manager: boolean;
}

interface FnfSettlement {
  id: number;
  separation_id: number;
  employee_id: number;
  leave_encashment: number | string;
  pending_salary: number | string;
  gratuity: number | string;
  bonus: number | string;
  deductions: number | string;
  loan_recovery: number | string;
  net_settlement: number | string;
  status: FnfStatus;
}

interface Separation {
  id: number;
  employee_id: number;
  employee_name: string;
  employee_code: string;
  dept_name: string | null;
  separation_type: string;
  resignation_date: string | null;
  last_working_day: string | null;
  notice_period_days: number | null;
  reason: string | null;
  status: SeparationStatus;
  clearance?: Clearance;
  fnfSettlements?: FnfSettlement[];
  created_at: string;
}

interface Loan {
  id: number;
  employee_id: number;
  employee_name: string;
  employee_code: string;
  loan_type: string;
  principal: number | string;
  outstanding: number | string;
  emi_amount: number | string | null;
  tenure_months: number | null;
  interest_rate: number | string | null;
  reason: string | null;
  status: LoanStatus;
}

interface LoanStats {
  totalOutstanding: number | string;
  activeCount: number;
  requestedCount: number;
  approvedCount: number;
}

interface OnboardingTask {
  id: number;
  employee_id: number;
  employee_name?: string;
  employee_code?: string;
  task_name: string;
  category: string | null;
  owner_role: string | null;
  due_date: string | null;
  status: OnboardingStatus;
  sequence: number;
}

const toNum = (v: any): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'string' ? parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : null;
};

const fmtDate = (v: any): string => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

/** Compact money, overflow-safe, full-precision on hover. */
const Money: React.FC<{ value: any; strong?: boolean }> = ({ value, strong }) => {
  const n = toNum(value);
  if (n === null) return <>—</>;
  return (
    <Typography
      component="span"
      title={formatCurrency(n)}
      sx={{
        fontVariantNumeric: 'tabular-nums',
        fontWeight: strong ? 700 : 400,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: 'inline-block',
        maxWidth: '100%',
      }}
    >
      {formatIndianNumber(n)}
    </Typography>
  );
};

const employeeLabel = (e: EmployeeListItem): string => `${e.first_name} ${e.last_name || ''} (${e.employee_code})`.trim();

// ---------------------------------------------------------------------------
// Constants — statuses/tones/transitions mirror validators in
// erp-api/src/validators/hrLifecycle.js exactly.
// ---------------------------------------------------------------------------

const SEPARATION_TYPES = ['RESIGNATION', 'TERMINATION', 'RETIREMENT', 'ABSCONDING', 'END_OF_CONTRACT'];
const SEPARATION_STATUS_ORDER: SeparationStatus[] = ['INITIATED', 'IN_CLEARANCE', 'PENDING_FNF', 'SETTLED'];
const SEPARATION_STATUS_TONE: Record<SeparationStatus, StatusTone> = {
  INITIATED: 'info',
  IN_CLEARANCE: 'warning',
  PENDING_FNF: 'warning',
  SETTLED: 'success',
  CANCELLED: 'neutral',
};
const SEPARATION_TYPE_TINT: Record<string, string> = {
  RESIGNATION: '#2563eb',
  TERMINATION: '#dc2626',
  RETIREMENT: '#7c3aed',
  ABSCONDING: '#b91c1c',
  END_OF_CONTRACT: '#64748b',
};
const nextSeparationStatus = (s: SeparationStatus): SeparationStatus | null => {
  const idx = SEPARATION_STATUS_ORDER.indexOf(s);
  return idx === -1 || idx === SEPARATION_STATUS_ORDER.length - 1 ? null : SEPARATION_STATUS_ORDER[idx + 1];
};

const FNF_STATUS_ORDER: FnfStatus[] = ['DRAFT', 'APPROVED', 'PAID'];
const FNF_STATUS_TONE: Record<FnfStatus, StatusTone> = { DRAFT: 'neutral', APPROVED: 'info', PAID: 'success' };
const nextFnfStatus = (s: FnfStatus): FnfStatus | null => {
  const idx = FNF_STATUS_ORDER.indexOf(s);
  return idx === -1 || idx === FNF_STATUS_ORDER.length - 1 ? null : FNF_STATUS_ORDER[idx + 1];
};

const LOAN_TYPES = ['LOAN', 'ADVANCE'];
const LOAN_STATUS_TONE: Record<LoanStatus, StatusTone> = {
  REQUESTED: 'warning',
  APPROVED: 'info',
  ACTIVE: 'success',
  CLOSED: 'neutral',
  REJECTED: 'error',
};
const LOAN_TRANSITIONS: Record<LoanStatus, LoanStatus[]> = {
  REQUESTED: ['APPROVED', 'REJECTED'],
  APPROVED: ['ACTIVE', 'REJECTED'],
  ACTIVE: ['CLOSED'],
  CLOSED: [],
  REJECTED: [],
};

const ONBOARD_STATUS_TONE: Record<OnboardingStatus, StatusTone> = {
  PENDING: 'neutral',
  IN_PROGRESS: 'warning',
  DONE: 'success',
  SKIPPED: 'error',
};

const CLEARANCE_FIELDS: { key: keyof Clearance; label: string }[] = [
  { key: 'asset', label: 'Asset Return' },
  { key: 'finance', label: 'Finance' },
  { key: 'it', label: 'IT Access' },
  { key: 'hr', label: 'HR' },
  { key: 'manager', label: 'Manager Sign-off' },
];

// ---------------------------------------------------------------------------
// Initiate Separation dialog
// ---------------------------------------------------------------------------

const InitiateSeparationDialog: React.FC<{
  open: boolean;
  employees: EmployeeListItem[];
  saving: boolean;
  onClose: () => void;
  onSubmit: (data: {
    employeeId: number;
    separationType: string;
    resignationDate: string;
    lastWorkingDay: string;
    noticePeriodDays: string;
    reason: string;
  }) => void;
}> = ({ open, employees, saving, onClose, onSubmit }) => {
  const [employee, setEmployee] = useState<EmployeeListItem | null>(null);
  const [separationType, setSeparationType] = useState('RESIGNATION');
  const [resignationDate, setResignationDate] = useState('');
  const [lastWorkingDay, setLastWorkingDay] = useState('');
  const [noticePeriodDays, setNoticePeriodDays] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setEmployee(null);
      setSeparationType('RESIGNATION');
      setResignationDate('');
      setLastWorkingDay('');
      setNoticePeriodDays('');
      setReason('');
      setError(null);
    }
  }, [open]);

  const handleSubmit = () => {
    if (!employee) {
      setError('Select an employee');
      return;
    }
    onSubmit({
      employeeId: employee.id,
      separationType,
      resignationDate,
      lastWorkingDay,
      noticePeriodDays,
      reason,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Initiate Separation</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mt: 0.25 }}>
          <Grid size={12}>
            <Autocomplete
              size="small"
              options={employees}
              value={employee}
              onChange={(_, v) => { setEmployee(v); if (v) setError(null); }}
              getOptionLabel={employeeLabel}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => (
                <TextField {...params} label="Employee" required error={!!error} helperText={error || ' '} />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="sep-type-label">Separation Type</InputLabel>
              <Select labelId="sep-type-label" label="Separation Type" value={separationType} onChange={(e) => setSeparationType(e.target.value)}>
                {SEPARATION_TYPES.map((t) => <MenuItem key={t} value={t}>{t.replace(/_/g, ' ')}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Notice Period (days)"
              type="number"
              fullWidth
              size="small"
              value={noticePeriodDays}
              onChange={(e) => setNoticePeriodDays(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Resignation Date"
              type="date"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={resignationDate}
              onChange={(e) => setResignationDate(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Last Working Day"
              type="date"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={lastWorkingDay}
              onChange={(e) => setLastWorkingDay(e.target.value)}
            />
          </Grid>
          <Grid size={12}>
            <TextField label="Reason" fullWidth size="small" multiline minRows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>{saving ? 'Saving…' : 'Initiate'}</Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Separation detail dialog — clearance checklist + F&F panel
// ---------------------------------------------------------------------------

interface FnfFormState {
  leaveEncashment: string;
  pendingSalary: string;
  gratuity: string;
  bonus: string;
  deductions: string;
  loanRecovery: string;
}

const emptyFnfForm: FnfFormState = {
  leaveEncashment: '',
  pendingSalary: '',
  gratuity: '',
  bonus: '',
  deductions: '',
  loanRecovery: '',
};

const SeparationDetailDialog: React.FC<{
  open: boolean;
  loading: boolean;
  separation: Separation | null;
  busy: boolean;
  onClose: () => void;
  onAdvanceStatus: (status: SeparationStatus) => void;
  onClearanceToggle: (key: keyof Clearance, value: boolean) => void;
  onCreateFnf: (data: FnfFormState) => void;
  onFnfStatus: (fnfId: number, status: FnfStatus) => void;
}> = ({ open, loading, separation, busy, onClose, onAdvanceStatus, onClearanceToggle, onCreateFnf, onFnfStatus }) => {
  const [fnfForm, setFnfForm] = useState<FnfFormState>(emptyFnfForm);

  useEffect(() => {
    if (open) setFnfForm(emptyFnfForm);
  }, [open, separation?.id]);

  const setField = <K extends keyof FnfFormState>(key: K, value: string) => setFnfForm((f) => ({ ...f, [key]: value }));

  const preview = useMemo(() => {
    const n = (v: string) => Number(v) || 0;
    return n(fnfForm.leaveEncashment) + n(fnfForm.pendingSalary) + n(fnfForm.gratuity) + n(fnfForm.bonus)
      - n(fnfForm.deductions) - n(fnfForm.loanRecovery);
  }, [fnfForm]);

  const next = separation ? nextSeparationStatus(separation.status) : null;
  const terminal = separation ? separation.status === 'SETTLED' || separation.status === 'CANCELLED' : true;
  const settlement = separation?.fnfSettlements && separation.fnfSettlements.length > 0 ? separation.fnfSettlements[0] : null;
  const fnfNext = settlement ? nextFnfStatus(settlement.status) : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {separation ? `${separation.employee_name} — ${separation.employee_code}` : 'Separation'}
      </DialogTitle>
      <DialogContent dividers>
        {loading || !separation ? (
          <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>
        ) : (
          <Stack spacing={3}>
            {/* Overview */}
            <Box>
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ mb: 1.5 }}>
                <TypePill label={separation.separation_type.replace(/_/g, ' ')} tint={SEPARATION_TYPE_TINT[separation.separation_type] || '#64748b'} />
                <StatusDot label={separation.status.replace(/_/g, ' ')} tone={SEPARATION_STATUS_TONE[separation.status]} />
              </Stack>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">Resignation Date</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmtDate(separation.resignation_date)}</Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">Last Working Day</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmtDate(separation.last_working_day)}</Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">Notice Period</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {separation.notice_period_days === null ? '—' : `${separation.notice_period_days} days`}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">Department</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{separation.dept_name || '—'}</Typography>
                </Grid>
                {separation.reason && (
                  <Grid size={12}>
                    <Typography variant="caption" color="text.secondary">Reason</Typography>
                    <Typography variant="body2">{separation.reason}</Typography>
                  </Grid>
                )}
              </Grid>
              <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
                {!terminal && next && (
                  <Button size="small" variant="contained" startIcon={<ArrowForward />} disabled={busy} onClick={() => onAdvanceStatus(next)}>
                    Advance to {next.replace(/_/g, ' ')}
                  </Button>
                )}
                {!terminal && (
                  <Button size="small" variant="outlined" color="error" startIcon={<CancelIcon />} disabled={busy} onClick={() => onAdvanceStatus('CANCELLED')}>
                    Cancel Separation
                  </Button>
                )}
              </Stack>
            </Box>

            <Divider />

            {/* Clearance checklist */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Clearance Checklist</Typography>
              <Grid container spacing={1}>
                {CLEARANCE_FIELDS.map((f) => (
                  <Grid key={f.key} size={{ xs: 12, sm: 6 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={!!separation.clearance?.[f.key]}
                          disabled={busy || terminal}
                          onChange={(e) => onClearanceToggle(f.key, e.target.checked)}
                        />
                      }
                      label={f.label}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider />

            {/* F&F panel */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Full &amp; Final Settlement</Typography>
              {settlement ? (
                <Stack spacing={1.5}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <StatusDot label={settlement.status} tone={FNF_STATUS_TONE[settlement.status]} />
                    {fnfNext && (
                      <Button size="small" variant="contained" disabled={busy} onClick={() => onFnfStatus(settlement.id, fnfNext)}>
                        Advance to {fnfNext}
                      </Button>
                    )}
                  </Stack>
                  <Grid container spacing={1.5}>
                    <Grid size={{ xs: 6, sm: 4 }}><Typography variant="caption" color="text.secondary">Leave Encashment</Typography><Box><Money value={settlement.leave_encashment} /></Box></Grid>
                    <Grid size={{ xs: 6, sm: 4 }}><Typography variant="caption" color="text.secondary">Pending Salary</Typography><Box><Money value={settlement.pending_salary} /></Box></Grid>
                    <Grid size={{ xs: 6, sm: 4 }}><Typography variant="caption" color="text.secondary">Gratuity</Typography><Box><Money value={settlement.gratuity} /></Box></Grid>
                    <Grid size={{ xs: 6, sm: 4 }}><Typography variant="caption" color="text.secondary">Bonus</Typography><Box><Money value={settlement.bonus} /></Box></Grid>
                    <Grid size={{ xs: 6, sm: 4 }}><Typography variant="caption" color="text.secondary">Deductions</Typography><Box><Money value={settlement.deductions} /></Box></Grid>
                    <Grid size={{ xs: 6, sm: 4 }}><Typography variant="caption" color="text.secondary">Loan Recovery</Typography><Box><Money value={settlement.loan_recovery} /></Box></Grid>
                    <Grid size={12}>
                      <Divider sx={{ my: 0.5 }} />
                      <Typography variant="caption" color="text.secondary">Net Settlement</Typography>
                      <Box><Money value={settlement.net_settlement} strong /></Box>
                    </Grid>
                  </Grid>
                </Stack>
              ) : (
                <Stack spacing={2}>
                  <Typography variant="caption" color="text.secondary">No F&amp;F settlement drafted yet.</Typography>
                  <Grid container spacing={1.5}>
                    <Grid size={{ xs: 6, sm: 4 }}>
                      <TextField label="Leave Encashment" type="number" size="small" fullWidth value={fnfForm.leaveEncashment} onChange={(e) => setField('leaveEncashment', e.target.value)} />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4 }}>
                      <TextField label="Pending Salary" type="number" size="small" fullWidth value={fnfForm.pendingSalary} onChange={(e) => setField('pendingSalary', e.target.value)} />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4 }}>
                      <TextField label="Gratuity" type="number" size="small" fullWidth value={fnfForm.gratuity} onChange={(e) => setField('gratuity', e.target.value)} />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4 }}>
                      <TextField label="Bonus" type="number" size="small" fullWidth value={fnfForm.bonus} onChange={(e) => setField('bonus', e.target.value)} />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4 }}>
                      <TextField label="Deductions" type="number" size="small" fullWidth value={fnfForm.deductions} onChange={(e) => setField('deductions', e.target.value)} />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4 }}>
                      <TextField label="Loan Recovery" type="number" size="small" fullWidth value={fnfForm.loanRecovery} onChange={(e) => setField('loanRecovery', e.target.value)} />
                    </Grid>
                  </Grid>
                  <Typography variant="body2">
                    Computed Net Settlement: <Typography component="span" sx={{ fontWeight: 700 }}>{formatIndianNumber(preview)}</Typography>
                  </Typography>
                  <Box>
                    <Button size="small" variant="contained" startIcon={<ReceiptLong />} disabled={busy} onClick={() => onCreateFnf(fnfForm)}>
                      Create F&amp;F Settlement
                    </Button>
                  </Box>
                </Stack>
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// New loan/advance dialog
// ---------------------------------------------------------------------------

const NewLoanDialog: React.FC<{
  open: boolean;
  employees: EmployeeListItem[];
  saving: boolean;
  onClose: () => void;
  onSubmit: (data: {
    employeeId: number;
    loanType: string;
    principal: string;
    emiAmount: string;
    tenureMonths: string;
    interestRate: string;
    reason: string;
  }) => void;
}> = ({ open, employees, saving, onClose, onSubmit }) => {
  const [employee, setEmployee] = useState<EmployeeListItem | null>(null);
  const [loanType, setLoanType] = useState('LOAN');
  const [principal, setPrincipal] = useState('');
  const [emiAmount, setEmiAmount] = useState('');
  const [tenureMonths, setTenureMonths] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setEmployee(null);
      setLoanType('LOAN');
      setPrincipal('');
      setEmiAmount('');
      setTenureMonths('');
      setInterestRate('');
      setReason('');
      setError(null);
    }
  }, [open]);

  const handleSubmit = () => {
    if (!employee) { setError('Select an employee'); return; }
    if (!principal || Number(principal) <= 0) { setError('Principal must be greater than 0'); return; }
    onSubmit({ employeeId: employee.id, loanType, principal, emiAmount, tenureMonths, interestRate, reason });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>New Loan / Advance</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mt: 0.25 }}>
          <Grid size={12}>
            <Autocomplete
              size="small"
              options={employees}
              value={employee}
              onChange={(_, v) => setEmployee(v)}
              getOptionLabel={employeeLabel}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => <TextField {...params} label="Employee" required />}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="loan-type-label">Type</InputLabel>
              <Select labelId="loan-type-label" label="Type" value={loanType} onChange={(e) => setLoanType(e.target.value)}>
                {LOAN_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Principal" type="number" required fullWidth size="small" value={principal} onChange={(e) => setPrincipal(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="EMI Amount" type="number" fullWidth size="small" value={emiAmount} onChange={(e) => setEmiAmount(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Tenure (months)" type="number" fullWidth size="small" value={tenureMonths} onChange={(e) => setTenureMonths(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Interest Rate (%)" type="number" fullWidth size="small" value={interestRate} onChange={(e) => setInterestRate(e.target.value)} />
          </Grid>
          <Grid size={12}>
            <TextField label="Reason" fullWidth size="small" multiline minRows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
          </Grid>
          {error && <Grid size={12}><Typography variant="caption" color="error">{error}</Typography></Grid>}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>{saving ? 'Saving…' : 'Create'}</Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Add onboarding task dialog
// ---------------------------------------------------------------------------

const AddTaskDialog: React.FC<{
  open: boolean;
  saving: boolean;
  onClose: () => void;
  onSubmit: (data: { taskName: string; category: string; ownerRole: string; dueDate: string }) => void;
}> = ({ open, saving, onClose, onSubmit }) => {
  const [taskName, setTaskName] = useState('');
  const [category, setCategory] = useState('');
  const [ownerRole, setOwnerRole] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) { setTaskName(''); setCategory(''); setOwnerRole(''); setDueDate(''); setError(null); }
  }, [open]);

  const handleSubmit = () => {
    if (!taskName.trim()) { setError('Task name is required'); return; }
    onSubmit({ taskName: taskName.trim(), category, ownerRole, dueDate });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Add Onboarding Task</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 0.25 }}>
          <TextField label="Task Name" required fullWidth size="small" value={taskName} error={!!error} helperText={error || ' '} onChange={(e) => setTaskName(e.target.value)} />
          <TextField label="Category" fullWidth size="small" value={category} onChange={(e) => setCategory(e.target.value)} />
          <TextField label="Owner Role" fullWidth size="small" value={ownerRole} onChange={(e) => setOwnerRole(e.target.value)} />
          <TextField label="Due Date" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>{saving ? 'Saving…' : 'Add Task'}</Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const TAB_LABELS = ['Separations', 'Loans & Advances', 'Onboarding'];

const LifecyclePage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  const [employees, setEmployees] = useState<EmployeeListItem[]>([]);
  const [separations, setSeparations] = useState<Separation[]>([]);
  const [loans, setLoans] = useState<Loan[]>([]);
  const [loanStats, setLoanStats] = useState<LoanStats | null>(null);
  const [overdueTasks, setOverdueTasks] = useState<OnboardingTask[]>([]);

  const [sepStatusFilter, setSepStatusFilter] = useState<SeparationStatus | 'ALL'>('ALL');
  const [loanStatusFilter, setLoanStatusFilter] = useState<LoanStatus | 'ALL'>('ALL');

  const [initiateOpen, setInitiateOpen] = useState(false);
  const [initiateSaving, setInitiateSaving] = useState(false);

  const [detailId, setDetailId] = useState<number | null>(null);
  const [detail, setDetail] = useState<Separation | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailBusy, setDetailBusy] = useState(false);

  const [loanDialogOpen, setLoanDialogOpen] = useState(false);
  const [loanSaving, setLoanSaving] = useState(false);
  const [loanBusyId, setLoanBusyId] = useState<number | null>(null);

  const [checklistEmployee, setChecklistEmployee] = useState<EmployeeListItem | null>(null);
  const [checklist, setChecklist] = useState<OnboardingTask[]>([]);
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [checklistBusy, setChecklistBusy] = useState(false);
  const [addTaskOpen, setAddTaskOpen] = useState(false);
  const [overdueBusyId, setOverdueBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [empRes, sepRes, loanRes, statsRes, overdueRes] = await Promise.all([
        hrService.getEmployees({ limit: 100, status: 'ACTIVE' }),
        hrPlusService.getSeparations(),
        hrPlusService.getLoans(),
        hrPlusService.loanStats(),
        hrPlusService.overdueOnboarding(),
      ]);
      setEmployees(empRes.employees || []);
      setSeparations(sepRes.separations || []);
      setLoans(loanRes.loans || []);
      setLoanStats(statsRes || null);
      setOverdueTasks(overdueRes.tasks || []);
    } catch (e: any) {
      setError(e.message || 'Failed to load lifecycle data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const reloadSeparations = useCallback(async () => {
    try {
      const r = await hrPlusService.getSeparations();
      setSeparations(r.separations || []);
    } catch { /* keep previous list on transient failure */ }
  }, []);

  const reloadLoans = useCallback(async () => {
    try {
      const [r, s] = await Promise.all([hrPlusService.getLoans(), hrPlusService.loanStats()]);
      setLoans(r.loans || []);
      setLoanStats(s || null);
    } catch { /* keep previous list */ }
  }, []);

  const reloadOverdue = useCallback(async () => {
    try {
      const r = await hrPlusService.overdueOnboarding();
      setOverdueTasks(r.tasks || []);
    } catch { /* keep previous list */ }
  }, []);

  // ---- Separations ----

  const filteredSeparations = useMemo(
    () => (sepStatusFilter === 'ALL' ? separations : separations.filter((s) => s.status === sepStatusFilter)),
    [separations, sepStatusFilter]
  );

  const handleInitiate = async (data: {
    employeeId: number; separationType: string; resignationDate: string; lastWorkingDay: string; noticePeriodDays: string; reason: string;
  }) => {
    setInitiateSaving(true);
    try {
      await hrPlusService.createSeparation({
        employeeId: data.employeeId,
        separationType: data.separationType,
        resignationDate: data.resignationDate || undefined,
        lastWorkingDay: data.lastWorkingDay || undefined,
        noticePeriodDays: data.noticePeriodDays ? Number(data.noticePeriodDays) : undefined,
        reason: data.reason || undefined,
      });
      setToast({ msg: 'Separation initiated', severity: 'success' });
      setInitiateOpen(false);
      await reloadSeparations();
    } catch (e: any) {
      setToast({ msg: e.message || 'Failed to initiate separation', severity: 'error' });
    } finally {
      setInitiateSaving(false);
    }
  };

  const openDetail = async (id: number) => {
    setDetailId(id);
    setDetailLoading(true);
    setDetail(null);
    try {
      const r = await hrPlusService.getSeparation(id);
      setDetail(r.separation || null);
    } catch (e: any) {
      setToast({ msg: e.message || 'Failed to load separation', severity: 'error' });
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshDetail = async (id: number) => {
    try {
      const r = await hrPlusService.getSeparation(id);
      setDetail(r.separation || null);
    } catch { /* keep previous detail */ }
  };

  const handleAdvanceStatus = async (status: SeparationStatus) => {
    if (!detailId) return;
    setDetailBusy(true);
    try {
      await hrPlusService.setSeparationStatus(detailId, status);
      setToast({ msg: `Separation moved to ${status.replace(/_/g, ' ')}`, severity: 'success' });
      await refreshDetail(detailId);
      await reloadSeparations();
    } catch (e: any) {
      setToast({ msg: e.message || 'Failed to update status', severity: 'error' });
    } finally {
      setDetailBusy(false);
    }
  };

  const handleClearanceToggle = async (key: keyof Clearance, value: boolean) => {
    if (!detailId) return;
    setDetailBusy(true);
    try {
      await hrPlusService.updateClearance(detailId, { [key]: value });
      await refreshDetail(detailId);
    } catch (e: any) {
      setToast({ msg: e.message || 'Failed to update clearance', severity: 'error' });
    } finally {
      setDetailBusy(false);
    }
  };

  const handleCreateFnf = async (form: FnfFormState) => {
    if (!detailId) return;
    setDetailBusy(true);
    try {
      await hrPlusService.createFnf(detailId, {
        leaveEncashment: Number(form.leaveEncashment) || 0,
        pendingSalary: Number(form.pendingSalary) || 0,
        gratuity: Number(form.gratuity) || 0,
        bonus: Number(form.bonus) || 0,
        deductions: Number(form.deductions) || 0,
        loanRecovery: form.loanRecovery === '' ? undefined : Number(form.loanRecovery),
      });
      setToast({ msg: 'F&F settlement drafted', severity: 'success' });
      await refreshDetail(detailId);
      await reloadSeparations();
    } catch (e: any) {
      setToast({ msg: e.message || 'Failed to create F&F settlement', severity: 'error' });
    } finally {
      setDetailBusy(false);
    }
  };

  const handleFnfStatus = async (fnfId: number, status: FnfStatus) => {
    if (!detailId) return;
    setDetailBusy(true);
    try {
      await hrPlusService.setFnfStatus(fnfId, status);
      setToast({ msg: `F&F settlement moved to ${status}`, severity: 'success' });
      await refreshDetail(detailId);
    } catch (e: any) {
      setToast({ msg: e.message || 'Failed to update F&F status', severity: 'error' });
    } finally {
      setDetailBusy(false);
    }
  };

  // ---- Loans ----

  const filteredLoans = useMemo(
    () => (loanStatusFilter === 'ALL' ? loans : loans.filter((l) => l.status === loanStatusFilter)),
    [loans, loanStatusFilter]
  );

  const handleCreateLoan = async (data: {
    employeeId: number; loanType: string; principal: string; emiAmount: string; tenureMonths: string; interestRate: string; reason: string;
  }) => {
    setLoanSaving(true);
    try {
      await hrPlusService.createLoan({
        employeeId: data.employeeId,
        loanType: data.loanType,
        principal: Number(data.principal),
        emiAmount: data.emiAmount ? Number(data.emiAmount) : undefined,
        tenureMonths: data.tenureMonths ? Number(data.tenureMonths) : undefined,
        interestRate: data.interestRate ? Number(data.interestRate) : undefined,
        reason: data.reason || undefined,
      });
      setToast({ msg: 'Loan/advance request created', severity: 'success' });
      setLoanDialogOpen(false);
      await reloadLoans();
    } catch (e: any) {
      setToast({ msg: e.message || 'Failed to create loan', severity: 'error' });
    } finally {
      setLoanSaving(false);
    }
  };

  const handleLoanStatus = async (id: number, status: LoanStatus) => {
    setLoanBusyId(id);
    try {
      await hrPlusService.setLoanStatus(id, status);
      setToast({ msg: `Loan moved to ${status}`, severity: 'success' });
      await reloadLoans();
    } catch (e: any) {
      setToast({ msg: e.message || 'Failed to update loan status', severity: 'error' });
    } finally {
      setLoanBusyId(null);
    }
  };

  // ---- Onboarding ----

  const loadChecklist = useCallback(async (employeeId: number) => {
    setChecklistLoading(true);
    try {
      const r = await hrPlusService.getOnboarding(employeeId);
      setChecklist(r.tasks || []);
    } catch (e: any) {
      setToast({ msg: e.message || 'Failed to load onboarding checklist', severity: 'error' });
    } finally {
      setChecklistLoading(false);
    }
  }, []);

  useEffect(() => {
    if (checklistEmployee) loadChecklist(checklistEmployee.id);
    else setChecklist([]);
  }, [checklistEmployee, loadChecklist]);

  const handleAddDefaultTemplate = async () => {
    if (!checklistEmployee) return;
    setChecklistBusy(true);
    try {
      await hrPlusService.addOnboarding(checklistEmployee.id, { template: 'default' });
      setToast({ msg: 'Default onboarding checklist added', severity: 'success' });
      await loadChecklist(checklistEmployee.id);
      await reloadOverdue();
    } catch (e: any) {
      setToast({ msg: e.message || 'Failed to add checklist', severity: 'error' });
    } finally {
      setChecklistBusy(false);
    }
  };

  const handleAddTask = async (data: { taskName: string; category: string; ownerRole: string; dueDate: string }) => {
    if (!checklistEmployee) return;
    setChecklistBusy(true);
    try {
      await hrPlusService.addOnboarding(checklistEmployee.id, {
        taskName: data.taskName,
        category: data.category || undefined,
        ownerRole: data.ownerRole || undefined,
        dueDate: data.dueDate || undefined,
      });
      setToast({ msg: 'Task added', severity: 'success' });
      setAddTaskOpen(false);
      await loadChecklist(checklistEmployee.id);
    } catch (e: any) {
      setToast({ msg: e.message || 'Failed to add task', severity: 'error' });
    } finally {
      setChecklistBusy(false);
    }
  };

  const handleChecklistStatus = async (id: number, status: OnboardingStatus) => {
    setChecklistBusy(true);
    try {
      await hrPlusService.setOnboardingStatus(id, status);
      if (checklistEmployee) await loadChecklist(checklistEmployee.id);
      await reloadOverdue();
    } catch (e: any) {
      setToast({ msg: e.message || 'Failed to update task', severity: 'error' });
    } finally {
      setChecklistBusy(false);
    }
  };

  const handleOverdueDone = async (id: number) => {
    setOverdueBusyId(id);
    try {
      await hrPlusService.setOnboardingStatus(id, 'DONE');
      await reloadOverdue();
      if (checklistEmployee) await loadChecklist(checklistEmployee.id);
    } catch (e: any) {
      setToast({ msg: e.message || 'Failed to update task', severity: 'error' });
    } finally {
      setOverdueBusyId(null);
    }
  };

  // ---- Render ----

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width={220} height={40} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={44} sx={{ mb: 2, borderRadius: 2 }} />
        <Skeleton variant="rounded" height={360} sx={{ borderRadius: 3 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <PageHeader title="Lifecycle Hub" subtitle="Separations, F&F, loans/advances and onboarding" />
        <TableSurface><ErrorState message={error} onRetry={load} /></TableSurface>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader title="Lifecycle Hub" subtitle="Separations, F&F, loans/advances and onboarding" />

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2.5, minHeight: 40, borderBottom: '1px solid', borderColor: 'divider', '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 700 } }}
      >
        {TAB_LABELS.map((t) => <Tab key={t} label={t} />)}
      </Tabs>

      {/* ---------------- Separations ---------------- */}
      {tab === 0 && (
        <Box>
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
            <Button variant="contained" startIcon={<PersonRemove />} onClick={() => setInitiateOpen(true)}>
              Initiate Separation
            </Button>
          </Stack>

          <FilterBar>
            {(['ALL', 'INITIATED', 'IN_CLEARANCE', 'PENDING_FNF', 'SETTLED', 'CANCELLED'] as const).map((s) => (
              <Chip
                key={s}
                label={s === 'ALL' ? `All (${separations.length})` : `${s.replace(/_/g, ' ')} (${separations.filter((x) => x.status === s).length})`}
                color={sepStatusFilter === s ? 'primary' : 'default'}
                variant={sepStatusFilter === s ? 'filled' : 'outlined'}
                onClick={() => setSepStatusFilter(s)}
              />
            ))}
          </FilterBar>

          <TableSurface>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Employee</TableCell>
                    <TableCell sx={headerCellSx}>Type</TableCell>
                    <TableCell sx={headerCellSx}>Resignation Date</TableCell>
                    <TableCell sx={headerCellSx}>Last Working Day</TableCell>
                    <TableCell sx={headerCellSx}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSeparations.length === 0 ? (
                    <TableRow><TableCell colSpan={5} sx={{ border: 0 }}><TableEmptyState title="No separations" hint="Initiate one to begin the exit workflow." /></TableCell></TableRow>
                  ) : (
                    filteredSeparations.map((s) => (
                      <TableRow key={s.id} hover sx={{ ...bodyRowSx, cursor: 'pointer' }} onClick={() => openDetail(s.id)}>
                        <TableCell sx={{ fontWeight: 600 }}>{s.employee_name} <Typography component="span" variant="caption" color="text.secondary">({s.employee_code})</Typography></TableCell>
                        <TableCell><TypePill label={s.separation_type.replace(/_/g, ' ')} tint={SEPARATION_TYPE_TINT[s.separation_type] || '#64748b'} /></TableCell>
                        <TableCell>{fmtDate(s.resignation_date)}</TableCell>
                        <TableCell>{fmtDate(s.last_working_day)}</TableCell>
                        <TableCell><StatusDot label={s.status.replace(/_/g, ' ')} tone={SEPARATION_STATUS_TONE[s.status]} /></TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>
        </Box>
      )}

      {/* ---------------- Loans & Advances ---------------- */}
      {tab === 1 && (
        <Box>
          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            <Grid size={{ xs: 6, md: 3 }}>
              <StatCard title="Total Outstanding" value={loanStats ? formatIndianNumber(toNum(loanStats.totalOutstanding) || 0) : null} icon={<AccountBalanceWallet />} color="#dc2626" />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <StatCard title="Active Loans" value={loanStats?.activeCount ?? null} icon={<CheckCircle />} color="#059669" />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <StatCard title="Requested" value={loanStats?.requestedCount ?? null} icon={<WarningAmber />} color="#d97706" />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <StatCard title="Approved" value={loanStats?.approvedCount ?? null} icon={<ArrowForward />} color="#2563eb" />
            </Grid>
          </Grid>

          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
            <Button variant="contained" startIcon={<Add />} onClick={() => setLoanDialogOpen(true)}>
              New Loan / Advance
            </Button>
          </Stack>

          <FilterBar>
            {(['ALL', 'REQUESTED', 'APPROVED', 'ACTIVE', 'CLOSED', 'REJECTED'] as const).map((s) => (
              <Chip
                key={s}
                label={s === 'ALL' ? `All (${loans.length})` : `${s} (${loans.filter((x) => x.status === s).length})`}
                color={loanStatusFilter === s ? 'primary' : 'default'}
                variant={loanStatusFilter === s ? 'filled' : 'outlined'}
                onClick={() => setLoanStatusFilter(s)}
              />
            ))}
          </FilterBar>

          <TableSurface>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Employee</TableCell>
                    <TableCell sx={headerCellSx}>Type</TableCell>
                    <TableCell sx={headerCellSx} align="right">Principal</TableCell>
                    <TableCell sx={headerCellSx} align="right">Outstanding</TableCell>
                    <TableCell sx={headerCellSx} align="right">EMI</TableCell>
                    <TableCell sx={headerCellSx}>Status</TableCell>
                    <TableCell sx={headerCellSx}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredLoans.length === 0 ? (
                    <TableRow><TableCell colSpan={7} sx={{ border: 0 }}><TableEmptyState title="No loans/advances" hint="Create one to record an employee loan or advance." /></TableCell></TableRow>
                  ) : (
                    filteredLoans.map((l) => (
                      <TableRow key={l.id} hover sx={bodyRowSx}>
                        <TableCell sx={{ fontWeight: 600 }}>{l.employee_name} <Typography component="span" variant="caption" color="text.secondary">({l.employee_code})</Typography></TableCell>
                        <TableCell><TypePill label={l.loan_type} tint={l.loan_type === 'ADVANCE' ? '#d97706' : '#2563eb'} /></TableCell>
                        <TableCell align="right"><Money value={l.principal} /></TableCell>
                        <TableCell align="right"><Money value={l.outstanding} strong /></TableCell>
                        <TableCell align="right"><Money value={l.emi_amount} /></TableCell>
                        <TableCell><StatusDot label={l.status} tone={LOAN_STATUS_TONE[l.status]} /></TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            {LOAN_TRANSITIONS[l.status].map((next) => (
                              <Button
                                key={next}
                                size="small"
                                variant="text"
                                disabled={loanBusyId === l.id}
                                color={next === 'REJECTED' ? 'error' : 'primary'}
                                onClick={() => handleLoanStatus(l.id, next)}
                              >
                                {next}
                              </Button>
                            ))}
                            {LOAN_TRANSITIONS[l.status].length === 0 && '—'}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>
        </Box>
      )}

      {/* ---------------- Onboarding ---------------- */}
      {tab === 2 && (
        <Grid container spacing={2.5}>
          <Grid size={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Overdue Onboarding (all employees)</Typography>
            <TableSurface>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headerCellSx}>Employee</TableCell>
                      <TableCell sx={headerCellSx}>Task</TableCell>
                      <TableCell sx={headerCellSx}>Category</TableCell>
                      <TableCell sx={headerCellSx}>Due Date</TableCell>
                      <TableCell sx={headerCellSx}>Status</TableCell>
                      <TableCell sx={headerCellSx}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {overdueTasks.length === 0 ? (
                      <TableRow><TableCell colSpan={6} sx={{ border: 0 }}><TableEmptyState title="Nothing overdue" hint="Every onboarding checklist is on track." /></TableCell></TableRow>
                    ) : (
                      overdueTasks.map((t) => (
                        <TableRow key={t.id} hover sx={bodyRowSx}>
                          <TableCell sx={{ fontWeight: 600 }}>{t.employee_name} <Typography component="span" variant="caption" color="text.secondary">({t.employee_code})</Typography></TableCell>
                          <TableCell>{t.task_name}</TableCell>
                          <TableCell>{t.category || '—'}</TableCell>
                          <TableCell sx={{ color: 'error.main', fontWeight: 600 }}>{fmtDate(t.due_date)}</TableCell>
                          <TableCell><StatusDot label={t.status.replace(/_/g, ' ')} tone={ONBOARD_STATUS_TONE[t.status]} /></TableCell>
                          <TableCell>
                            <Button size="small" disabled={overdueBusyId === t.id} onClick={() => handleOverdueDone(t.id)}>Mark Done</Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </TableSurface>
          </Grid>

          <Grid size={12}>
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Employee Checklist</Typography>
                <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap">
                  <Autocomplete
                    size="small"
                    options={employees}
                    value={checklistEmployee}
                    onChange={(_, v) => setChecklistEmployee(v)}
                    getOptionLabel={employeeLabel}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    sx={{ minWidth: 280 }}
                    renderInput={(params) => <TextField {...params} label="Employee" placeholder="Search employee…" />}
                  />
                  <Button variant="outlined" startIcon={<HowToReg />} disabled={!checklistEmployee || checklistBusy} onClick={handleAddDefaultTemplate}>
                    Add Default Template
                  </Button>
                  <Button variant="contained" startIcon={<Add />} disabled={!checklistEmployee} onClick={() => setAddTaskOpen(true)}>
                    Add Task
                  </Button>
                </Stack>
              </Stack>

              {!checklistEmployee ? (
                <TableEmptyState title="No employee selected" hint="Pick an employee to view or build their onboarding checklist." />
              ) : checklistLoading ? (
                <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>
              ) : checklist.length === 0 ? (
                <TableEmptyState title="No onboarding tasks yet" hint="Add the default template or a custom task to get started." />
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headerCellSx}>Task</TableCell>
                        <TableCell sx={headerCellSx}>Category</TableCell>
                        <TableCell sx={headerCellSx}>Owner</TableCell>
                        <TableCell sx={headerCellSx}>Due Date</TableCell>
                        <TableCell sx={headerCellSx}>Status</TableCell>
                        <TableCell sx={headerCellSx}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {checklist.map((t) => (
                        <TableRow key={t.id} hover sx={bodyRowSx}>
                          <TableCell sx={{ fontWeight: 600 }}>{t.task_name}</TableCell>
                          <TableCell>{t.category || '—'}</TableCell>
                          <TableCell>{t.owner_role || '—'}</TableCell>
                          <TableCell>{fmtDate(t.due_date)}</TableCell>
                          <TableCell><StatusDot label={t.status.replace(/_/g, ' ')} tone={ONBOARD_STATUS_TONE[t.status]} /></TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap">
                              {t.status !== 'IN_PROGRESS' && t.status !== 'DONE' && (
                                <Button size="small" disabled={checklistBusy} onClick={() => handleChecklistStatus(t.id, 'IN_PROGRESS')}>Start</Button>
                              )}
                              {t.status !== 'DONE' && (
                                <Button size="small" color="success" disabled={checklistBusy} onClick={() => handleChecklistStatus(t.id, 'DONE')}>Done</Button>
                              )}
                              {t.status !== 'SKIPPED' && t.status !== 'DONE' && (
                                <Button size="small" color="error" disabled={checklistBusy} onClick={() => handleChecklistStatus(t.id, 'SKIPPED')}>Skip</Button>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* ---------------- Dialogs ---------------- */}
      <InitiateSeparationDialog
        open={initiateOpen}
        employees={employees}
        saving={initiateSaving}
        onClose={() => setInitiateOpen(false)}
        onSubmit={handleInitiate}
      />

      <SeparationDetailDialog
        open={detailId !== null}
        loading={detailLoading}
        separation={detail}
        busy={detailBusy}
        onClose={() => { setDetailId(null); setDetail(null); }}
        onAdvanceStatus={handleAdvanceStatus}
        onClearanceToggle={handleClearanceToggle}
        onCreateFnf={handleCreateFnf}
        onFnfStatus={handleFnfStatus}
      />

      <NewLoanDialog
        open={loanDialogOpen}
        employees={employees}
        saving={loanSaving}
        onClose={() => setLoanDialogOpen(false)}
        onSubmit={handleCreateLoan}
      />

      <AddTaskDialog
        open={addTaskOpen}
        saving={checklistBusy}
        onClose={() => setAddTaskOpen(false)}
        onSubmit={handleAddTask}
      />

      <Snackbar open={!!toast} autoHideDuration={3200} onClose={() => setToast(null)}>
        {toast ? <Alert severity={toast.severity} variant="filled" onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default LifecyclePage;
